/**
 * AgroTech — Ask Nabhos  (Step 9 of the 9-step intelligence loop)
 *
 * Conversational NLQ interface — the user types a question in plain English,
 * the backend resolves the intent + boundary + date range, assembles grounded
 * DB context, and returns:
 *   • narrative   — plain-English AI answer
 *   • dataTable   — tabular anomaly / imagery data supporting the answer
 *   • mapHighlight — boundary + anomaly IDs to highlight on a map
 *   • suggestedActions — next steps for the ops team
 *
 * Sessions are preserved server-side.  The user can load a previous session
 * from the left-panel history list.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Sparkles, Clock, ChevronDown, ChevronUp, RotateCcw, MessageSquare } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { conversationApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DataRow {
  date: string;
  anomalyType?: string;
  severity?: string;
  changePercent?: string | number;
  status?: string;
  affectedHectares?: number | null;
}

interface MapHighlight {
  boundaryId: string;
  boundaryName: string;
  anomalyIds: string[];
  severity: string | null;
}

interface Turn {
  id?: string;
  role: 'user' | 'assistant';
  text: string;
  mapHighlight?: MapHighlight | null;
  dataTable?: DataRow[];
  suggestedActions?: string[];
  loading?: boolean;
}

interface Session {
  id: string;
  title: string;
  boundary_id: string | null;
  updated_at: string;
}

// ─── Severity badge ───────────────────────────────────────────────────────────

const SEVERITY_COLOURS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  high:     'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  medium:   'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  low:      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

function SeverityBadge({ severity }: { severity?: string }) {
  if (!severity) return null;
  const cls = SEVERITY_COLOURS[severity.toLowerCase()] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${cls}`}>
      {severity}
    </span>
  );
}

// ─── Suggested-action pill ────────────────────────────────────────────────────

function ActionPill({ text }: { text: string }) {
  return (
    <span className="inline-block px-3 py-1 rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 text-xs border border-violet-200 dark:border-violet-800">
      {text}
    </span>
  );
}

// ─── Data table collapse/expand ───────────────────────────────────────────────

function DataTable({ rows }: { rows: DataRow[] }) {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;

  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {open ? 'Hide' : 'Show'} supporting data ({rows.length} record{rows.length !== 1 ? 's' : ''})
      </button>

      {open && (
        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Type</th>
                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Severity</th>
                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Change</th>
                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Status</th>
                <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Area (ha)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((row, i) => (
                <tr key={i} className="bg-white dark:bg-gray-900">
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.date}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 capitalize">{row.anomalyType ?? '—'}</td>
                  <td className="px-3 py-2"><SeverityBadge severity={row.severity} /></td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.changePercent != null ? `${row.changePercent}%` : '—'}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 capitalize">{row.status ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.affectedHectares ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Chat bubble ──────────────────────────────────────────────────────────────

function ChatBubble({ turn }: { turn: Turn }) {
  const isUser = turn.role === 'user';

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-violet-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 text-sm leading-relaxed">
          {turn.text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 items-start">
      {/* Avatar */}
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center mt-0.5">
        <Sparkles className="w-3.5 h-3.5 text-white" />
      </div>

      <div className="flex-1 min-w-0">
        {turn.loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2">
            <Spinner size="sm" />
            <span>Nabhos is thinking…</span>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl rounded-tl-sm px-4 py-3">
            {/* Narrative */}
            <p className="text-sm text-gray-800 dark:text-gray-100 leading-relaxed whitespace-pre-wrap">
              {turn.text}
            </p>

            {/* Data table */}
            {turn.dataTable && turn.dataTable.length > 0 && (
              <DataTable rows={turn.dataTable} />
            )}

            {/* Map highlight notice */}
            {turn.mapHighlight && (
              <div className="mt-3 flex items-center gap-2 text-xs text-indigo-600 dark:text-indigo-400">
                <span className="w-2 h-2 rounded-full bg-indigo-400 inline-block" />
                Boundary highlighted: <strong>{turn.mapHighlight.boundaryName}</strong>
                {turn.mapHighlight.anomalyIds.length > 0 && (
                  <span className="text-gray-400 dark:text-gray-500">
                    — {turn.mapHighlight.anomalyIds.length} anomal{turn.mapHighlight.anomalyIds.length !== 1 ? 'ies' : 'y'}
                  </span>
                )}
              </div>
            )}

            {/* Suggested actions */}
            {turn.suggestedActions && turn.suggestedActions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {turn.suggestedActions.map((a, i) => <ActionPill key={i} text={a} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Suggested starter queries ────────────────────────────────────────────────

const STARTERS = [
  'Show me anomalies in East Block Tea Garden since March',
  'How has NDVI changed over the last 3 months?',
  'What drone missions were completed last month?',
  'Which fields have critical-severity alerts?',
];

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function ConversationalAnalysisPage() {
  const [turns, setTurns]                 = useState<Turn[]>([]);
  const [input, setInput]                 = useState('');
  const [sending, setSending]             = useState(false);
  const [error, setError]                 = useState('');
  const [sessionId, setSessionId]         = useState<string | null>(null);
  const [sessions, setSessions]           = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showHistory, setShowHistory]     = useState(false);
  const bottomRef                         = useRef<HTMLDivElement>(null);
  const inputRef                          = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns]);

  // Load session list on first mount
  useEffect(() => {
    (async () => {
      try {
        setLoadingSessions(true);
        const res = await conversationApi.listSessions();
        setSessions(res.data ?? []);
      } catch {
        // non-fatal
      } finally {
        setLoadingSessions(false);
      }
    })();
  }, []);

  const loadSession = useCallback(async (id: string) => {
    try {
      const res = await conversationApi.getSessionTurns(id);
      const loaded: Turn[] = (res.data ?? []).flatMap((t) => [
        { role: 'user' as const,      text: t.user_query },
        { role: 'assistant' as const, text: t.response?.narrative ?? '', mapHighlight: t.response?.mapHighlight, dataTable: t.response?.dataTable, suggestedActions: t.response?.suggestedActions },
      ]);
      setTurns(loaded);
      setSessionId(id);
      setShowHistory(false);
    } catch {
      setError('Failed to load session history.');
    }
  }, []);

  const submit = useCallback(async (queryText: string) => {
    const q = queryText.trim();
    if (!q || sending) return;

    setError('');
    setSending(true);

    // Optimistically append user turn + loading assistant placeholder
    setTurns((prev) => [
      ...prev,
      { role: 'user',      text: q },
      { role: 'assistant', text: '', loading: true },
    ]);
    setInput('');

    try {
      const res = await conversationApi.ask(q, sessionId);
      const d   = res.data;

      setSessionId(d.sessionId);

      // Replace loading placeholder with real answer
      setTurns((prev) => {
        const updated = [...prev];
        // findLastIndex polyfill for targets < ES2023
        let idx = -1;
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].role === 'assistant' && updated[i].loading) { idx = i; break; }
        }
        if (idx >= 0) {
          updated[idx] = {
            id:               d.turnId,
            role:             'assistant',
            text:             d.narrative,
            mapHighlight:     d.mapHighlight,
            dataTable:        d.dataTable,
            suggestedActions: d.suggestedActions,
          };
        }
        return updated;
      });

      // Refresh session list
      try {
        const sRes = await conversationApi.listSessions();
        setSessions(sRes.data ?? []);
      } catch { /* non-fatal */ }
    } catch (err: any) {
      setTurns((prev) => prev.filter((t) => !(t.role === 'assistant' && t.loading)));
      setError(err?.response?.data?.error ?? 'Failed to get a response. Please try again.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [sending, sessionId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const startNewSession = () => {
    setTurns([]);
    setSessionId(null);
    setError('');
    inputRef.current?.focus();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-4rem)] bg-white dark:bg-gray-950">

      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">Ask Nabhos</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Step 9 — Conversational intelligence across all data</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Session history toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1.5 text-xs"
          >
            <Clock className="w-3.5 h-3.5" />
            History
          </Button>

          {/* New session */}
          {turns.length > 0 && (
            <Button variant="ghost" size="sm" onClick={startNewSession} className="flex items-center gap-1.5 text-xs">
              <RotateCcw className="w-3.5 h-3.5" />
              New
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0">

        {/* ── Session history panel ─────────────────────────────────────── */}
        {showHistory && (
          <div className="w-64 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex flex-col">
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Recent sessions</p>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {loadingSessions ? (
                <div className="flex justify-center py-4"><Spinner size="sm" /></div>
              ) : sessions.length === 0 ? (
                <p className="px-3 py-4 text-xs text-gray-400 dark:text-gray-500">No previous sessions yet.</p>
              ) : (
                sessions.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => loadSession(s.id)}
                    className={`w-full text-left px-3 py-2.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-800 ${sessionId === s.id ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    <div className="font-medium truncate">{s.title || 'Untitled session'}</div>
                    <div className="text-gray-400 dark:text-gray-500 mt-0.5">{new Date(s.updated_at).toLocaleDateString()}</div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Chat area ─────────────────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-h-0">

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">

            {/* Empty state */}
            {turns.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center gap-6 py-12">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                  <MessageSquare className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ask anything about your fields</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
                    Nabhos searches your satellite imagery, anomalies, and drone missions to answer in plain English.
                  </p>
                </div>

                {/* Starter query pills */}
                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      onClick={() => submit(s)}
                      className="px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300 transition-colors border border-gray-200 dark:border-gray-700"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Turns */}
            {turns.map((turn, i) => (
              <ChatBubble key={i} turn={turn} />
            ))}

            {/* Error */}
            {error && (
              <Alert type="error">{error}</Alert>
            )}

            {/* Scroll anchor */}
            <div ref={bottomRef} />
          </div>

          {/* ── Input area ─────────────────────────────────────────────── */}
          <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-4 py-3">
            <div className="flex items-end gap-2 max-w-3xl mx-auto">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about anomalies, NDVI trends, missions… (Enter to send)"
                rows={1}
                className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow max-h-32 overflow-y-auto"
                style={{ minHeight: '42px' }}
                disabled={sending}
                autoFocus
              />
              <Button
                onClick={() => submit(input)}
                disabled={!input.trim() || sending}
                className="flex-shrink-0 w-10 h-10 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:bg-gray-200 dark:disabled:bg-gray-700 flex items-center justify-center transition-colors"
              >
                {sending ? (
                  <Spinner size="sm" />
                ) : (
                  <Send className="w-4 h-4 text-white" />
                )}
              </Button>
            </div>
            <p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-2">
              Nabhos answers are grounded in your organisation's data only.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}

// Made with Bob
